import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { openPrintWindow } from '@/utils/printHelper';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Check, ChevronsUpDown, ArrowLeft, Printer, FileText, Building2, Package, Calendar, RefreshCw, Plus, Trash2, Edit, X, StickyNote, Mail, Loader2, Upload, Image } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default Terms and Conditions
const DEFAULT_TERMS = [
  'Quotation is valid for 30 days from the date of issue.',
  'Prices are inclusive of all applicable taxes.',
  'Report any defects within 7 days for replacement.',
  'Die and Stereo cost will be charged extra if applicable.',
  'Delivery within 7-10 working days from order confirmation.'
];

const CreateQuotationForm = () => {
  const navigate = useNavigate();
  
  // Company selection
  const [customers, setCustomers] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [companyOpen, setCompanyOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Quotation selection
  const [availableQuotations, setAvailableQuotations] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  
  // Form details
  const [quotationDate, setQuotationDate] = useState(new Date().toISOString().split('T')[0]);
  const [quotationNumber, setQuotationNumber] = useState('');
  
  // Terms and Conditions
  const [terms, setTerms] = useState([...DEFAULT_TERMS]);
  const [newTerm, setNewTerm] = useState('');
  const [editingTermIndex, setEditingTermIndex] = useState(null);
  const [editingTermText, setEditingTermText] = useState('');
  
  // Notes section
  const [notesEnabled, setNotesEnabled] = useState(false);
  const [notes, setNotes] = useState('');
  
  // Signatory image
  const [signatoryImage, setSignatoryImage] = useState(null);
  const [signatoryPreview, setSignatoryPreview] = useState(null);
  
  // Loading state
  const [loading, setLoading] = useState(false);
  // Indicates the operator has hand-edited the quotation number so we don't
  // silently overwrite their value during background refreshes.
  const [manualOverride, setManualOverride] = useState(false);

  useEffect(() => {
    fetchCustomers();
    generateQuotationNumber();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchQuotationsForCompany(selectedCompany);
      const customer = customers.find(c => c.company_name?.toLowerCase() === selectedCompany.toLowerCase());
      setSelectedCustomer(customer);
    } else {
      setAvailableQuotations([]);
      setSelectedCustomer(null);
    }
    setSelectedItems([]);
  }, [selectedCompany, customers]);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/wo-customers`);
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const generateQuotationNumber = async ({ silent = false } = {}) => {
    try {
      const response = await axios.get(`${API}/quotations`);
      let maxNum = 0;
      response.data.forEach(q => {
        if (q.quotation_number && q.quotation_number.startsWith('QTCPI-')) {
          const numPart = parseInt(q.quotation_number.replace('QTCPI-', ''), 10);
          if (!isNaN(numPart) && numPart > maxNum) {
            maxNum = numPart;
          }
        }
      });
      const next = `QTCPI-${String(maxNum + 1).padStart(4, '0')}`;
      setQuotationNumber(next);
      // Once the user has typed something manually we still respect that —
      // but for first-load + after-save flows we always advance.
      setManualOverride(false);
      if (!silent) {
        // tiny visual confirmation via toast (only on explicit refresh button click)
      }
    } catch (error) {
      setQuotationNumber('QTCPI-0001');
      setManualOverride(false);
    }
  };

  const fetchQuotationsForCompany = async (companyName) => {
    try {
      const response = await axios.get(`${API}/quotations`);
      const filtered = response.data.filter(q => 
        q.company_name?.toLowerCase() === companyName.toLowerCase() &&
        (q.status === 'in_process' || q.status === 'pending' || q.status === 'available')
      );
      setAvailableQuotations(filtered);
    } catch (error) {
      console.error('Error fetching quotations:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const addItem = (quotation) => {
    if (!selectedItems.find(item => item.id === quotation.id)) {
      // Build default description
      const defaultDesc = buildDescription(quotation);
      setSelectedItems([...selectedItems, { 
        ...quotation, 
        qty: quotation.moq || 1,
        customDescription: defaultDesc
      }]);
    }
  };

  // Add a free-form / manual line item (not tied to a calculation)
  const addManualItem = () => {
    const id = `manual_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    setSelectedItems(prev => [
      ...prev,
      {
        id,
        isManual: true,
        item_name: "",
        customDescription: "",
        qty: 1,
        rate: 0,
        hsn: "4819",
      },
    ]);
  };

  // Resolve the per-unit rate for any item type.
  const getItemRate = (item) => {
    if (item?.isManual) return parseFloat(item.rate) || 0;
    return parseFloat(item?.calculated_values?.finalBoxPrice || 0);
  };

  const updateItemField = (id, field, value) => {
    setSelectedItems(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const removeItem = (id) => {
    setSelectedItems(selectedItems.filter(item => item.id !== id));
  };

  const updateItemQty = (id, qty) => {
    setSelectedItems(selectedItems.map(item => 
      item.id === id ? { ...item, qty: parseFloat(qty) || 1 } : item
    ));
  };

  const updateItemDescription = (id, description) => {
    setSelectedItems(selectedItems.map(item => 
      item.id === id ? { ...item, customDescription: description } : item
    ));
  };

  const calculateSubTotal = () => {
    return selectedItems.reduce((sum, item) => {
      return sum + (getItemRate(item) * (item.qty || 1));
    }, 0);
  };

  // Terms and Conditions functions
  const addTerm = () => {
    if (newTerm.trim()) {
      setTerms([...terms, newTerm.trim()]);
      setNewTerm('');
    }
  };

  const deleteTerm = (index) => {
    setTerms(terms.filter((_, i) => i !== index));
  };

  const startEditTerm = (index) => {
    setEditingTermIndex(index);
    setEditingTermText(terms[index]);
  };

  const saveTerm = () => {
    if (editingTermText.trim() && editingTermIndex !== null) {
      const newTerms = [...terms];
      newTerms[editingTermIndex] = editingTermText.trim();
      setTerms(newTerms);
      setEditingTermIndex(null);
      setEditingTermText('');
    }
  };

  const cancelEditTerm = () => {
    setEditingTermIndex(null);
    setEditingTermText('');
  };

  // Number to words helper
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    
    const numToWords = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + numToWords(n % 100) : '');
      if (n < 100000) return numToWords(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + numToWords(n % 1000) : '');
      if (n < 10000000) return numToWords(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + numToWords(n % 100000) : '');
      return numToWords(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + numToWords(n % 10000000) : '');
    };
    
    const rupees = Math.floor(num);
    const paise = Math.round((num - rupees) * 100);
    
    let result = numToWords(rupees);
    if (paise > 0) {
      result += ' and ' + numToWords(paise) + ' Paise';
    }
    return result;
  };

  // Build description without Joint and Reel x Cutting
  const buildDescription = (quotation) => {
    if (!quotation) return '';
    
    const parts = [];
    
    if (quotation.ply_count) parts.push(`${quotation.ply_count} Ply`);
    if (quotation.production_type) parts.push(quotation.production_type);
    if (quotation.dimensions) {
      const { length, breadth, height, unit } = quotation.dimensions;
      if (length || breadth || height) {
        parts.push(`${length || 0} × ${breadth || 0} × ${height || 0} ${unit || 'mm'}`);
      }
    }
    if (quotation.moq) parts.push(`MOQ: ${quotation.moq}`);
    
    return parts.join(' | ');
  };

  const printQuotation = () => {
    if (selectedItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    const subTotal = calculateSubTotal();
    // Use the public logo path
    const logoUrl = `${window.location.origin}/logo.png`;

    const htmlContent = `
      <html>
        <head>
          <title>Quotation - ${quotationNumber}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
            body { font-family: Arial, sans-serif; padding: 20px; color: #333; background: #fff; }
            .container { max-width: 800px; margin: 0 auto; border: 2px solid #1a365d; }
            
            /* Header */
            .header { background: #1a365d !important; color: white !important; padding: 20px; display: flex; justify-content: space-between; align-items: flex-start; }
            .logo-section { display: flex; align-items: center; gap: 15px; }
            .logo { width: 70px; height: 70px; background: white; border-radius: 8px; padding: 5px; object-fit: contain; }
            .company-info h1 { font-size: 20px; margin-bottom: 5px; color: white !important; }
            .company-info p { font-size: 10px; opacity: 0.9; line-height: 1.4; color: white !important; }
            .doc-info { text-align: right; }
            .doc-info h2 { font-size: 24px; letter-spacing: 2px; margin-bottom: 10px; color: #ffd700 !important; }
            .doc-info .doc-number { font-size: 14px; background: rgba(255,255,255,0.2) !important; padding: 5px 12px; border-radius: 3px; display: inline-block; margin-bottom: 5px; color: white !important; }
            .doc-info .doc-date { font-size: 11px; opacity: 0.9; color: white !important; }
            
            /* To Section */
            .to-section { background: #f0f7ff !important; padding: 15px 20px; border-bottom: 2px solid #1a365d; }
            .to-section h3 { font-size: 11px; color: #1a365d !important; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 1px; font-weight: bold; }
            .to-section .company-name { font-size: 16px; font-weight: bold; color: #1a365d !important; }
            .to-section .address { font-size: 11px; color: #555 !important; margin-top: 3px; }
            
            /* Item Table */
            .item-table { width: 100%; border-collapse: collapse; }
            .item-table th { background: #1a365d !important; color: white !important; padding: 12px 10px; text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
            .item-table td { padding: 12px 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; vertical-align: top; }
            .item-table tr:nth-child(even) { background: #f8fafc !important; }
            .item-table .item-name { font-weight: 600; color: #1a365d !important; font-size: 12px; }
            .item-table .item-desc { color: #555 !important; font-size: 10px; margin-top: 4px; }
            .item-table .text-right { text-align: right; }
            .item-table .text-center { text-align: center; }
            
            /* Totals */
            .totals-section { padding: 15px 20px; background: #f8fafc !important; }
            .totals-row { display: flex; justify-content: flex-end; margin-bottom: 8px; }
            .totals-label { width: 150px; text-align: right; padding-right: 20px; font-size: 12px; color: #555 !important; }
            .totals-value { width: 120px; text-align: right; font-size: 12px; font-weight: 500; }
            .grand-total { background: #1a365d !important; color: white !important; padding: 12px 20px; margin: 0 -20px; }
            .grand-total .totals-label, .grand-total .totals-value { color: white !important; font-size: 14px; font-weight: bold !important; }
            
            /* Amount in Words */
            .amount-words { padding: 15px 20px; background: #e6f0ff !important; border-top: 2px solid #1a365d; }
            .amount-words strong { color: #1a365d !important; font-size: 11px; }
            .amount-words span { font-size: 11px; color: #333 !important; }
            
            /* Terms */
            .terms { padding: 20px; border-top: 1px solid #e2e8f0; }
            .terms h3 { font-size: 12px; color: #1a365d !important; margin-bottom: 10px; text-transform: uppercase; letter-spacing: 0.5px; }
            .terms ul { font-size: 10px; color: #555 !important; padding-left: 18px; line-height: 1.8; }
            
            /* Notes */
            .notes { padding: 15px 20px; background: #fffbeb !important; border-top: 1px solid #f59e0b; }
            .notes h3 { font-size: 12px; color: #b45309 !important; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
            .notes p { font-size: 11px; color: #555 !important; line-height: 1.6; }
            
            /* Signature */
            .signature { padding: 30px 20px 20px; text-align: right; border-top: 1px solid #e2e8f0; }
            .signature-line { border-top: 2px solid #1a365d; width: 200px; margin-left: auto; padding-top: 8px; font-size: 11px; color: #1a365d !important; font-weight: 600; }
            
            /* Footer */
            .footer { background: #1a365d !important; color: white !important; text-align: center; font-size: 9px; padding: 10px; }
            
            @media print {
              body { padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .container { max-width: 100%; border: none; }
              .header { background: #1a365d !important; -webkit-print-color-adjust: exact !important; }
              .item-table th { background: #1a365d !important; -webkit-print-color-adjust: exact !important; }
              .grand-total { background: #1a365d !important; -webkit-print-color-adjust: exact !important; }
              .footer { background: #1a365d !important; -webkit-print-color-adjust: exact !important; }
              .to-section { background: #f0f7ff !important; -webkit-print-color-adjust: exact !important; }
              .amount-words { background: #e6f0ff !important; -webkit-print-color-adjust: exact !important; }
              .notes { background: #fffbeb !important; -webkit-print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <div class="logo-section">
                <img src="${logoUrl}" class="logo" onerror="this.style.display='none'" />
                <div class="company-info">
                  <h1>Challenge Packaging Industries</h1>
                  <p>Tamil Nadu, India<br/>
                  Phone: 7299808164<br/>
                  Email: info@challengepack.in<br/>
                  GSTIN: 33AGSPV7578C2ZP</p>
                </div>
              </div>
              <div class="doc-info">
                <h2>QUOTATION</h2>
                <div class="doc-number">${quotationNumber}</div>
                <div class="doc-date">Date: ${formatDate(quotationDate)}</div>
              </div>
            </div>

            <div class="to-section">
              <h3>To</h3>
              <div class="company-name">${selectedCompany || '-'}</div>
              ${selectedCustomer?.address ? `<div class="address">${selectedCustomer.address}</div>` : ''}
              ${selectedCustomer?.gstin ? `<div class="address">GSTIN: ${selectedCustomer.gstin}</div>` : ''}
            </div>

            <table class="item-table">
              <thead>
                <tr>
                  <th style="width: 5%">#</th>
                  <th style="width: 45%">Item & Description</th>
                  <th style="width: 12%" class="text-center">HSN</th>
                  <th style="width: 10%" class="text-center">Qty</th>
                  <th style="width: 14%" class="text-right">Rate</th>
                  <th style="width: 14%" class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${selectedItems.map((item, idx) => `
                  <tr>
                    <td class="text-center">${idx + 1}</td>
                    <td>
                      <div class="item-name">${item.item_name || item.box_name || '-'}</div>
                      <div class="item-desc">${item.customDescription || (item.isManual ? '' : buildDescription(item))}</div>
                    </td>
                    <td class="text-center">${item.hsn || '4819'}</td>
                    <td class="text-center">${item.qty}</td>
                    <td class="text-right">₹${getItemRate(item).toFixed(2)}</td>
                    <td class="text-right">₹${(getItemRate(item) * item.qty).toFixed(2)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>

            <div class="totals-section">
              <div class="totals-row">
                <div class="totals-label">Sub Total</div>
                <div class="totals-value">₹${subTotal.toFixed(2)}</div>
              </div>
              <div class="totals-row grand-total">
                <div class="totals-label">Grand Total</div>
                <div class="totals-value">₹${subTotal.toFixed(2)}</div>
              </div>
            </div>

            <div class="amount-words">
              <strong>Amount in Words: </strong>
              <span>Indian Rupees ${numberToWords(subTotal)} Only</span>
            </div>

            <div class="terms">
              <h3>Terms & Conditions</h3>
              <ul>
                ${terms.map(term => `<li>${term}</li>`).join('')}
              </ul>
            </div>

            ${notesEnabled && notes.trim() ? `
              <div class="notes">
                <h3>Notes</h3>
                <p>${notes.replace(/\n/g, '<br/>')}</p>
              </div>
            ` : ''}

            <div class="signature">
              <div class="signature-line">Authorized Signature</div>
            </div>

            <div class="footer">
              Challenge Packaging Industries | This is a computer-generated quotation
            </div>
          </div>
          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
    `;
    openPrintWindow(htmlContent);
  };

  // Handle signatory image upload
  const handleSignatoryUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Signatory image must be less than 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignatoryImage(reader.result);
        setSignatoryPreview(reader.result);
      };
      reader.readAsDataURL(file);
      toast.success('Signatory image uploaded');
    }
  };

  const removeSignatory = () => {
    setSignatoryImage(null);
    setSignatoryPreview(null);
  };

  // Send Email function - sends email with PDF attachment via backend
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  // CC and BCC — comma-separated input that's split into a list on send.
  // Added Feb 2026 at customer request.
  const [ccEmails, setCcEmails] = useState('');
  const [bccEmails, setBccEmails] = useState('');
  const [senderName, setSenderName] = useState('Sundari Kasi - Asst.Manager - Challenge Packaging Industries');
  const [senderOpen, setSenderOpen] = useState(false);
  const [previousEmails, setPreviousEmails] = useState([]);
  const [emailContent, setEmailContent] = useState('');
  const [showEmailSuggestions, setShowEmailSuggestions] = useState(false);
  
  // Sender options
  const SENDER_OPTIONS = [
    'Bhuvaneswaran Veeravel - Manager - Challenge Packaging Industries',
    'Sundari Kasi - Asst.Manager - Challenge Packaging Industries'
  ];

  // Fetch previous emails for company
  const fetchPreviousEmails = async (companyName) => {
    try {
      const response = await axios.get(`${API}/customers/emails/${encodeURIComponent(companyName)}`);
      setPreviousEmails(response.data.emails || []);
    } catch (error) {
      setPreviousEmails([]);
    }
  };

  // Save email to customer record
  const saveEmailToCustomer = async (companyName, email) => {
    try {
      await axios.post(`${API}/customers/emails`, {
        company_name: companyName,
        email: email
      });
    } catch (error) {
      console.error('Failed to save email:', error);
    }
  };

  // Generate default email content
  const generateDefaultEmailContent = () => {
    const itemNames = selectedItems.map(item => item.item_name || item.box_name).join(', ');
    return `Dear ${recipientName || selectedCompany || 'Sir/Madam'},

The quotation (${quotationNumber}) is attached with this email.

An overview of the quotation is available below:

Items: ${itemNames}
Total Amount: ₹${calculateSubTotal().toFixed(2)}

Please go through it and confirm the order. We look forward to working with you again.`;
  };
  
  const openEmailDialog = () => {
    if (selectedItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    // Pre-fill recipient name with customer name
    setRecipientName(selectedCompany || '');
    // Get customer email if available
    if (selectedCustomer?.email) {
      setRecipientEmail(selectedCustomer.email);
    }
    // Fetch previous emails for this company
    if (selectedCompany) {
      fetchPreviousEmails(selectedCompany);
    }
    // Set default email content
    setEmailContent(generateDefaultEmailContent());
    setEmailDialogOpen(true);
  };

  const sendEmail = async () => {
    if (!recipientEmail) {
      toast.error('Please enter recipient email address');
      return;
    }
    
    if (selectedItems.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setSendingEmail(true);
    
    try {
      const subTotal = calculateSubTotal();
      
      // Build items array for API
      const itemsData = selectedItems.map((item) => ({
        item_name: item.item_name || item.box_name,
        description: item.customDescription || (item.isManual ? '' : buildDescription(item)),
        qty: item.qty,
        rate: getItemRate(item),
        amount: getItemRate(item) * item.qty,
      }));
      
      // Get all item names for subject line
      const itemNames = selectedItems.map(item => item.item_name || item.box_name).join(', ');
      
      // Split CC/BCC by comma/semicolon/whitespace and drop empties
      const splitEmails = (raw) => (raw || '')
        .split(/[,;\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const ccList = splitEmails(ccEmails);
      const bccList = splitEmails(bccEmails);

      // Send email via backend API
      const response = await axios.post(`${API}/quotations/send-email`, {
        to_email: recipientEmail,
        to_name: recipientName || selectedCompany,
        cc: ccList,
        bcc: bccList,
        quotation_number: quotationNumber,
        customer_name: selectedCompany,
        quotation_date: formatDate(quotationDate),
        total_amount: subTotal,
        validity_days: 30,
        items: itemsData,
        terms: terms,
        item_names: itemNames,
        sender_name: senderName,
        custom_content: emailContent,
        signatory_image: signatoryImage
      });
      
      // Save this email address for future suggestions
      if (selectedCompany && recipientEmail) {
        saveEmailToCustomer(selectedCompany, recipientEmail);
      }
      
      toast.success(`Email sent successfully to ${recipientEmail}`);
      setEmailDialogOpen(false);
      setRecipientEmail('');
      setRecipientName('');
      setEmailContent('');
      // Advance the quotation number for the next entry. This was previously
      // missing — the form retained the same number until the user clicked
      // the refresh icon or navigated away and back.
      generateQuotationNumber({ silent: true });
    } catch (error) {
      console.error('Failed to send email:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to send email. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/workorder#quotation')} className="border-gray-600 text-gray-300 hover:bg-gray-800">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-amber-400 flex items-center gap-3">
              <FileText className="h-8 w-8 text-amber-400" />
              Create Quotation
            </h1>
            <p className="text-gray-400 mt-1">Generate quotation from existing box calculations</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={generateQuotationNumber} className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
            <RefreshCw className="h-4 w-4 mr-2" />
            New QT #
          </Button>
          <Button 
            onClick={openEmailDialog}
            disabled={selectedItems.length === 0}
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-semibold px-6 py-3 rounded-lg shadow-lg disabled:opacity-50"
            data-testid="send-email-btn"
          >
            <Mail className="h-5 w-5 mr-2" />
            Send Email
          </Button>
          <Button 
            onClick={printQuotation}
            disabled={selectedItems.length === 0}
            className="bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 text-white font-semibold px-6 py-3 rounded-lg shadow-lg disabled:opacity-50"
            data-testid="print-quotation-btn"
          >
            <Printer className="h-5 w-5 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* Main Form - 3 Column Layout like PO */}
      <div className="grid grid-cols-3 gap-6">
        {/* Company Info */}
        <Card className="bg-gray-900/80 border-amber-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-amber-400 flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Company Info
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <p className="text-white font-bold text-lg">CHALLENGE PACKAGING INDUSTRIES</p>
            <p className="text-gray-400 text-sm">Tamil Nadu, India</p>
            <p className="text-gray-400 text-sm">Phone: 7299808164</p>
            <p className="text-gray-400 text-sm">Email: info@challengepack.in</p>
            <p className="text-gray-400 text-sm">GSTIN: 33AGSPV7578C2ZP</p>
          </CardContent>
        </Card>

        {/* Customer Details */}
        <Card className="bg-gray-900/80 border-amber-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-amber-400 flex items-center gap-2">
              <Package className="h-5 w-5" />
              Customer Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-300 text-sm">Customer Name *</Label>
              <Popover open={companyOpen} onOpenChange={setCompanyOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between bg-gray-800 border-gray-600 text-white mt-1">
                    {selectedCompany || "Select customer..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 bg-gray-900 border-gray-700">
                  <Command className="bg-gray-900">
                    <CommandInput placeholder="Search customer..." className="text-white" />
                    <CommandList>
                      <CommandEmpty>No customer found.</CommandEmpty>
                      <CommandGroup>
                        {customers.map((customer) => (
                          <CommandItem key={customer.id} value={customer.company_name} onSelect={(value) => { setSelectedCompany(value); setCompanyOpen(false); }} className="text-white hover:bg-gray-800">
                            <Check className={`mr-2 h-4 w-4 ${selectedCompany === customer.company_name ? "opacity-100" : "opacity-0"}`} />
                            {customer.company_name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            {selectedCustomer && (
              <>
                {selectedCustomer.office_address && <p className="text-gray-400 text-sm">{selectedCustomer.office_address}</p>}
                {selectedCustomer.gstin && <p className="text-gray-400 text-sm">GSTIN: {selectedCustomer.gstin}</p>}
              </>
            )}
          </CardContent>
        </Card>

        {/* Quotation Details */}
        <Card className="bg-gray-900/80 border-amber-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-amber-400 flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Quotation Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-300 text-sm flex items-center justify-between">
                <span>Quotation Number</span>
                <span className="text-[10px] font-normal text-gray-500">
                  {manualOverride
                    ? "Manual override — auto-increment paused"
                    : "Auto — editable"}
                </span>
              </Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={quotationNumber}
                  onChange={(e) => {
                    setQuotationNumber(e.target.value);
                    setManualOverride(true);
                  }}
                  className="bg-gray-800 border-gray-600 text-cyan-400 font-mono"
                  data-testid="quotation-number-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => generateQuotationNumber()}
                  title="Reset to next auto-generated number"
                  className="border-gray-600 text-gray-300"
                  data-testid="quotation-number-refresh-btn"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Format: QTCPI-XXXX. Tap the refresh icon to recompute, or type any value.
              </p>
            </div>
            <div>
              <Label className="text-gray-300 text-sm">Quotation Date</Label>
              <Input type="date" value={quotationDate} onChange={(e) => setQuotationDate(e.target.value)} className="bg-gray-800 border-gray-600 text-white mt-1" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Add Available Items */}
      {selectedCompany && availableQuotations.length > 0 && (
        <Card className="bg-gray-900/80 border-amber-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-amber-400 flex items-center gap-2">
              Quick Add Available Items
              <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded">{availableQuotations.length} items</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {availableQuotations.map((q) => (
                <Button 
                  key={q.id}
                  variant="outline"
                  size="sm"
                  onClick={() => addItem(q)}
                  disabled={selectedItems.find(item => item.id === q.id)}
                  className={`border-green-500/30 text-green-400 hover:bg-green-500/10 ${selectedItems.find(item => item.id === q.id) ? 'opacity-50' : ''}`}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  {q.item_name || q.box_name} (₹{q.calculated_values?.finalBoxPrice || '0'})
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {selectedCompany && availableQuotations.length === 0 && (
        <Card className="bg-yellow-900/20 border-yellow-500/30">
          <CardContent className="py-6 text-center">
            <p className="text-yellow-400">No in-process quotations found for this customer.</p>
            <p className="text-gray-400 text-sm mt-1">Please create a new quotation first using the "New Quotation" button on the Quotation page.</p>
          </CardContent>
        </Card>
      )}

      {/* Line Items */}
      <Card className="bg-gray-900/80 border-amber-500/30">
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-lg text-amber-400 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Line Items
          </CardTitle>
          <Button
            onClick={addManualItem}
            size="sm"
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="quotation-add-manual-item"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Manual Item
          </Button>
        </CardHeader>
        <CardContent>
          {selectedItems.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p>No items added yet.</p>
              <p className="text-sm mt-1">Pick a calculation above, or click <b className="text-cyan-300">Add Manual Item</b> to enter a free-form line.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-gray-700">
                  <TableHead className="text-gray-300">#</TableHead>
                  <TableHead className="text-gray-300">Item Name</TableHead>
                  <TableHead className="text-gray-300">Description (Editable)</TableHead>
                  <TableHead className="text-gray-300 text-center">Qty</TableHead>
                  <TableHead className="text-gray-300 text-right">Rate</TableHead>
                  <TableHead className="text-gray-300 text-right">Amount</TableHead>
                  <TableHead className="text-gray-300"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedItems.map((item, idx) => (
                  <TableRow key={item.id} className="border-gray-700" data-testid={`quotation-line-${idx + 1}`}>
                    <TableCell className="text-gray-400">{idx + 1}</TableCell>
                    <TableCell className="text-white font-medium">
                      {item.isManual ? (
                        <Input
                          value={item.item_name || ""}
                          onChange={(e) => updateItemField(item.id, "item_name", e.target.value)}
                          className="bg-gray-800 border-gray-600 text-white text-sm"
                          placeholder="Item name…"
                          data-testid={`quotation-line-name-${idx + 1}`}
                        />
                      ) : (
                        <span>{item.item_name || item.box_name}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input 
                        value={item.customDescription || ''} 
                        onChange={(e) => updateItemDescription(item.id, e.target.value)}
                        className="bg-gray-800 border-gray-600 text-gray-300 text-sm"
                        placeholder={item.isManual ? "Description…" : "Edit description..."}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <Input type="number" value={item.qty} onChange={(e) => updateItemQty(item.id, e.target.value)} className="w-20 bg-gray-800 border-gray-600 text-white text-center" min="1" />
                    </TableCell>
                    <TableCell className="text-right text-gray-300">
                      {item.isManual ? (
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.rate}
                          onChange={(e) => updateItemField(item.id, "rate", e.target.value)}
                          className="w-28 bg-gray-800 border-gray-600 text-white text-right ml-auto"
                          placeholder="Rate"
                          data-testid={`quotation-line-rate-${idx + 1}`}
                        />
                      ) : (
                        <>₹{getItemRate(item).toFixed(2)}</>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-green-400 font-bold">₹{(getItemRate(item) * (parseFloat(item.qty) || 0)).toFixed(2)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => removeItem(item.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          
          {/* Totals */}
          {selectedItems.length > 0 && (
            <div className="mt-6 flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-gray-300">
                  <span>Sub Total</span>
                  <span>₹{calculateSubTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-green-400 border-t border-gray-700 pt-2">
                  <span>Grand Total</span>
                  <span>₹{calculateSubTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Terms and Conditions */}
      <Card className="bg-gray-900/80 border-amber-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-amber-400 flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Terms & Conditions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Terms List */}
          <div className="space-y-2">
            {terms.map((term, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg">
                <span className="text-gray-400 text-sm w-6">{idx + 1}.</span>
                {editingTermIndex === idx ? (
                  <>
                    <Input 
                      value={editingTermText} 
                      onChange={(e) => setEditingTermText(e.target.value)}
                      className="flex-1 bg-gray-700 border-gray-600 text-white text-sm"
                    />
                    <Button variant="ghost" size="sm" onClick={saveTerm} className="text-green-400 hover:text-green-300 hover:bg-green-500/10">
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={cancelEditTerm} className="text-gray-400 hover:text-gray-300 hover:bg-gray-500/10">
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 text-gray-300 text-sm">{term}</span>
                    <Button variant="ghost" size="sm" onClick={() => startEditTerm(idx)} className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10">
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => deleteTerm(idx)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
            ))}
          </div>
          
          {/* Add New Term */}
          <div className="flex gap-2">
            <Input 
              value={newTerm} 
              onChange={(e) => setNewTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addTerm()}
              placeholder="Add new term..."
              className="flex-1 bg-gray-800 border-gray-600 text-white"
            />
            <Button onClick={addTerm} disabled={!newTerm.trim()} className="bg-amber-600 hover:bg-amber-500 text-white">
              <Plus className="h-4 w-4 mr-1" />
              Add
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Notes Section */}
      <Card className="bg-gray-900/80 border-amber-500/30">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-amber-400 flex items-center gap-2">
              <StickyNote className="h-5 w-5" />
              Notes
            </CardTitle>
            <div className="flex items-center gap-3">
              <Label className="text-gray-400 text-sm">Enable Notes</Label>
              <Switch checked={notesEnabled} onCheckedChange={setNotesEnabled} />
            </div>
          </div>
        </CardHeader>
        {notesEnabled && (
          <CardContent>
            <Textarea 
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes that will appear on the quotation..."
              className="bg-gray-800 border-gray-600 text-white min-h-[100px]"
            />
            <p className="text-xs text-gray-500 mt-2">Notes will be displayed in a highlighted section on the printed quotation.</p>
          </CardContent>
        )}
      </Card>

      {/* Authorized Signatory Section */}
      <Card className="bg-gray-900/80 border-purple-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg text-purple-400 flex items-center gap-2">
            <Image className="h-5 w-5" />
            Authorized Signatory
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <Label className="text-gray-300">Upload Signature Image</Label>
              <p className="text-xs text-gray-500 mt-1 mb-3">Upload an image of the authorized signature (PNG, JPG - max 2MB)</p>
              <div className="flex items-center gap-3">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSignatoryUpload}
                    className="hidden"
                    data-testid="signatory-upload-input"
                  />
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg hover:border-purple-500 transition-colors">
                    <Upload className="h-4 w-4 text-purple-400" />
                    <span className="text-gray-300">Choose File</span>
                  </div>
                </label>
                {signatoryPreview && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={removeSignatory}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
            {signatoryPreview && (
              <div className="p-3 bg-gray-800/50 rounded-lg border border-purple-500/30">
                <p className="text-xs text-gray-400 mb-2">Preview:</p>
                <img 
                  src={signatoryPreview} 
                  alt="Signatory Preview" 
                  className="max-h-20 max-w-40 object-contain"
                />
              </div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-3">This signature will appear on the PDF attachment sent via email.</p>
        </CardContent>
      </Card>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md bg-gray-950 border-2 border-blue-500/30">
          <DialogHeader>
            <DialogTitle className="text-xl text-blue-400 flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Send Quotation via Email
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Send {quotationNumber} to customer with PDF attachment
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label className="text-gray-300">Recipient Name</Label>
              <Input 
                value={recipientName} 
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Enter recipient name..."
                className="bg-gray-800 border-gray-600 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-gray-300">Recipient Email *</Label>
              <Input 
                type="email"
                value={recipientEmail} 
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="Enter email address..."
                className="bg-gray-800 border-gray-600 text-white mt-1"
                data-testid="recipient-email-input"
              />
              {/* Previous email suggestions */}
              {previousEmails.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-1">Previously used emails:</p>
                  <div className="flex flex-wrap gap-1">
                    {previousEmails.map((email, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setRecipientEmail(email)}
                        className={`px-2 py-1 text-xs rounded border ${
                          recipientEmail === email 
                            ? 'bg-blue-500/20 border-blue-500 text-blue-400' 
                            : 'bg-gray-800 border-gray-600 text-gray-400 hover:border-gray-500'
                        }`}
                      >
                        {email}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            {/* CC and BCC inputs — comma-separated, optional. Added Feb 2026. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-gray-300">CC <span className="text-gray-500 text-xs">(comma-separated)</span></Label>
                <Input
                  value={ccEmails}
                  onChange={(e) => setCcEmails(e.target.value)}
                  placeholder="cc1@example.com, cc2@example.com"
                  className="bg-gray-800 border-gray-600 text-white mt-1"
                  data-testid="quotation-email-cc-input"
                />
              </div>
              <div>
                <Label className="text-gray-300">BCC <span className="text-gray-500 text-xs">(hidden)</span></Label>
                <Input
                  value={bccEmails}
                  onChange={(e) => setBccEmails(e.target.value)}
                  placeholder="bcc1@example.com, bcc2@example.com"
                  className="bg-gray-800 border-gray-600 text-white mt-1"
                  data-testid="quotation-email-bcc-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-gray-300">Sender *</Label>
              <Popover open={senderOpen} onOpenChange={setSenderOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between bg-gray-800 border-gray-600 text-white mt-1">
                    {senderName?.split(' - ')[0] || "Select sender..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0 bg-gray-900 border-gray-700">
                  <Command className="bg-gray-900">
                    <CommandList>
                      <CommandGroup>
                        {SENDER_OPTIONS.map((sender) => (
                          <CommandItem 
                            key={sender} 
                            value={sender} 
                            onSelect={() => { setSenderName(sender); setSenderOpen(false); }} 
                            className="text-white hover:bg-gray-800"
                          >
                            <Check className={`mr-2 h-4 w-4 ${senderName === sender ? "opacity-100" : "opacity-0"}`} />
                            <div>
                              <p className="font-medium">{sender.split(' - ')[0]}</p>
                              <p className="text-xs text-gray-400">{sender.split(' - ').slice(1).join(' - ')}</p>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            
            {/* Editable Email Content */}
            <div>
              <Label className="text-gray-300">Email Content</Label>
              <Textarea 
                value={emailContent}
                onChange={(e) => setEmailContent(e.target.value)}
                placeholder="Email message..."
                className="bg-gray-800 border-gray-600 text-white mt-1 min-h-[150px]"
              />
              <p className="text-xs text-gray-500 mt-1">You can modify the email content before sending</p>
            </div>
            
            {/* Summary Preview */}
            <div className="p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <p className="text-gray-400 text-sm mb-2">Email Summary:</p>
              <p className="text-cyan-400 font-mono text-sm">{quotationNumber}</p>
              <p className="text-white text-sm">Customer: {selectedCompany}</p>
              <p className="text-white text-sm">Items: {selectedItems.map(item => item.item_name || item.box_name).join(', ')}</p>
              <p className="text-green-400 text-sm font-bold">Total: ₹{calculateSubTotal().toFixed(2)}</p>
              <p className="text-gray-400 text-xs mt-2">• PDF attachment included (same as print format)</p>
              <p className="text-gray-400 text-xs">• Sender: {senderName?.split(' - ')[0]}</p>
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => { setEmailDialogOpen(false); setRecipientEmail(''); setRecipientName(''); setEmailContent(''); setCcEmails(''); setBccEmails(''); }} 
                className="border-gray-600 text-gray-300"
                disabled={sendingEmail}
              >
                Cancel
              </Button>
              <Button 
                onClick={sendEmail} 
                className="bg-blue-600 hover:bg-blue-500 text-white"
                disabled={sendingEmail || !recipientEmail}
                data-testid="confirm-send-email-btn"
              >
                {sendingEmail ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateQuotationForm;
